import * as React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { NewStyleSheet } from "../../assets/icons";
import {
  Button,
  Divider,
  Menu,
  MenuPopover,
  MenuTrigger,
  SpinButton,
  Switch,
  Tooltip,
} from "@fluentui/react-components";
import CommonTooltip from "../common/CommonTooltip";
import { ArrowsClockwiseIcon, CaretDownIcon, GearIcon, QuestionIcon, WarningIcon, XIcon } from "@phosphor-icons/react";
import { useSelector } from "react-redux";
import {
  brightness,
  getDominantColor,
  hexToRgb,
  hslToHex,
  objToHex,
  rgbToHSL,
  transformColor,
} from "../../helpers/colors";
import PreviewTable from "./PreviewTable";
import { InfoIcon } from "@phosphor-icons/react/dist/ssr";
import { updateSheet } from "../../state/styleSheetSlice";
import { useDispatch } from "react-redux";

const StyleSheetWizard = ({
  detectedFonts,
  availableFonts,
  detectedColors,
  existingColors,
  selectedSheetKey,
  onResult,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [useHSLSpace, setUseHSLSpace] = React.useState(true);
  const [hslDiff, setHSLDiff] = React.useState(20);
  const [rgbDiff, setRGBDiff] = React.useState(0.2);
  const [hslNegDiff, setHSLNegDiff] = React.useState(20);
  const [rgbNegDiff, setRGBNegDiff] = React.useState(0.3);
  const [useOnlyFirstColor, setUseOnlyFirstColor] = React.useState(false);
  const [brightnessCondition, setBrightnessCondition] = React.useState(186);

  const selectedSheet = useSelector(
    (state) =>
      state.styleSheet.draftSheets[selectedSheetKey] ??
      state.styleSheet.sheets[selectedSheetKey] ??
      state.styleSheet.sheets["default"]
  );

  const isKeyEditable = !selectedSheet || selectedSheetKey === "default";
  const [suggestedSheetKey, setSuggestedSheetKey] = React.useState(
    isKeyEditable ? `Custom ${getDominantColor(hexToRgb(detectedColors[2]?.key))}` : selectedSheetKey
  );

  const [suggestedText1, setSuggestedText1] = React.useState(0);
  const [suggestedText2, setSuggestedText2] = React.useState(1);
  const [suggestedHighlight, setSuggestedHighlight] = React.useState(2);
  const [suggestedSecondary, setSuggestedSecondary] = React.useState(3);
  const [shadingColor, setShadingColor] = React.useState("#C0C0C0");

  const supplementalColors = [
    { key: "#067EEC", count: 0 },
    { key: "#12A525", count: 0 },
    { key: "#444444", count: 0 },
    { key: "#666666", count: 0 },
    { key: "#BA22C7", count: 0 },
    { key: "#D11E21", count: 0 },
    { key: "#EB8C00", count: 0 },
    { key: "#FFD000", count: 0 },
  ];

  const availableColors = detectedColors.length < 5 ? [...detectedColors, ...supplementalColors] : [...detectedColors];
  const [suggestedColors, setSuggestedColors] = React.useState(getNewSuggestedColors(0, 1, 2, 3, "#C0C0C0"));

  function getNewSuggestedColors(
    text1Index,
    text2Index,
    highlightIndex,
    secondaryIndex,
    shadingColor = "#C0C0C0",
    useHSLSpace = true,
    hslDiff = 20,
    rgbDiff = 0.2,
    hslNegDiff,
    rgbNegDiff,
    brightnessCondition = 186,
    useOnlyFirstColor = false
  ) {
    let highlight1 = "";
    let highlight2 = "";
    const hc = availableColors[highlightIndex]?.key;
    const { r, g, b } = hexToRgb(hc);
    const br = brightness(r, g, b);
    const hsl = rgbToHSL(r, g, b);
    if (br <= brightnessCondition) {
      highlight1 = hc;
      if (useHSLSpace) {
        highlight2 = hslToHex(hsl.h, hsl.s, Math.min(hsl.l + hslDiff, 100));
      } else {
        highlight2 = objToHex(transformColor(r, g, b, 1 + rgbDiff));
      }
    } else {
      highlight2 = hc;
      if (useHSLSpace) {
        highlight1 = hslToHex(hsl.h, hsl.s, Math.max(hsl.l - (hslNegDiff ?? hslDiff), 0));
      } else {
        highlight1 = objToHex(transformColor(r, g, b, 1 - (rgbNegDiff ?? rgbDiff)));
      }
    }

    let secondary1 = "";
    let secondary2 = "";
    if (useOnlyFirstColor) {
      if (br <= brightnessCondition) {
        if (useHSLSpace) {
          secondary1 = hslToHex(hsl.h, hsl.s, Math.min(hsl.l + 2 * hslDiff, 100));
        } else {
          secondary1 = objToHex(transformColor(r, g, b, 1 + 2 * rgbDiff));
        }
        if (useHSLSpace) {
          secondary2 = hslToHex(hsl.h, hsl.s, Math.min(hsl.l + 3 * hslDiff, 100));
        } else {
          secondary2 = objToHex(transformColor(r, g, b, 1 + 3 * rgbDiff));
        }
      } else {
        if (useHSLSpace) {
          secondary2 = hslToHex(hsl.h, hsl.s, Math.max(hsl.l - 2 * (hslNegDiff ?? hslDiff), 0));
        } else {
          secondary2 = objToHex(transformColor(r, g, b, 1 - 2 * (rgbNegDiff ?? rgbDiff)));
        }
        if (useHSLSpace) {
          secondary1 = hslToHex(hsl.h, hsl.s, Math.max(hsl.l - 3 * (hslNegDiff ?? hslDiff), 0));
        } else {
          secondary1 = objToHex(transformColor(r, g, b, 1 - 3 * (rgbNegDiff ?? rgbDiff)));
        }
      }
    } else {
      const sc = availableColors[secondaryIndex]?.key;
      const rgb = hexToRgb(sc);
      const br2 = brightness(rgb.r, rgb.g, rgb.b);
      const hsl2 = rgbToHSL(rgb.r, rgb.g, rgb.b);
      if (br2 <= brightnessCondition) {
        secondary1 = sc;
        if (useHSLSpace) {
          secondary2 = hslToHex(hsl2.h, hsl2.s, Math.min(hsl2.l + hslDiff, 100));
        } else {
          secondary2 = objToHex(transformColor(rgb.r, rgb.g, rgb.b, 1 + rgbDiff));
        }
      } else {
        secondary2 = sc;
        if (useHSLSpace) {
          secondary1 = hslToHex(hsl2.h, hsl2.s, Math.max(hsl2.l - (hslNegDiff ?? hslDiff), 0));
        } else {
          secondary1 = objToHex(transformColor(rgb.r, rgb.g, rgb.b, 1 - (rgbNegDiff ?? rgbDiff)));
        }
      }
    }
    // console.log("New colors", {
    //   highlight: {
    //     highlight1,
    //     highlight2,
    //     hc,
    //     rgb: { r, g, b },
    //     br,
    //     hsl,
    //     modifiedHsl1: { h: hsl.h, s: hsl.s, l: Math.min(hsl.l + hslDiff, 100) },
    //     modifiedHsl2: { h: hsl.h, s: hsl.s, l: Math.max(hsl.l - (hslNegDiff ?? hslDiff), 0) },
    //   },
    //   secondary: {
    //     secondary1,
    //     secondary2,
    //     sc,
    //     rgb,
    //     br2,
    //     hsl2,
    //     modifiedHsl1: { h: hsl2.h, s: hsl2.s, l: Math.min(hsl2.l + hslDiff, 100) },
    //     modifiedHsl2: { h: hsl2.h, s: hsl2.s, l: Math.max(hsl2.l - (hslNegDiff ?? hslDiff), 0) },
    //   },
    // });

    // Shading 2 is the user-selected base color (default #C0C0C0 = HSL(0,0,75))
    // Shading 1 is auto-generated as Shading 2 with L+20
    const shading2 = shadingColor;
    const { r: sr, g: sg, b: sb } = hexToRgb(shading2);
    const shadingHsl = rgbToHSL(sr, sg, sb);
    const shading1 = hslToHex(shadingHsl.h, shadingHsl.s, Math.min(shadingHsl.l + 20, 100));

    return existingColors.map((color) => {
      switch (color.name) {
        // case "text":
        //   return { ...color, newValue: text1 !== "" ? text1 : undefined };
        // case "text2":
        //   return { ...color, newValue: text2 !== "" ? text2 : undefined };
        case "accent1":
          return { ...color, newValue: highlight1 !== "" ? highlight1 : undefined };
        case "accent2":
          return { ...color, newValue: highlight2 !== "" ? highlight2 : undefined };
        case "secondary1":
          return { ...color, newValue: secondary1 !== "" ? secondary1 : undefined };
        case "secondary2":
          return { ...color, newValue: secondary2 !== "" ? secondary2 : undefined };
        case "shading1":
          return { ...color, newValue: shading1 !== "" ? shading1 : undefined };
        case "shading2":
          return { ...color, newValue: shading2 !== "" ? shading2 : undefined };
        default:
          return { ...color };
      }
    });
  }

  React.useEffect(() => {
    setSuggestedColors(
      getNewSuggestedColors(
        suggestedText1,
        suggestedText2,
        suggestedHighlight,
        suggestedSecondary,
        shadingColor,
        useHSLSpace,
        hslDiff,
        rgbDiff,
        hslNegDiff,
        rgbNegDiff,
        brightnessCondition,
        useOnlyFirstColor
      )
    );
  }, [
    suggestedText1,
    suggestedText2,
    suggestedHighlight,
    suggestedSecondary,
    shadingColor,
    useHSLSpace,
    hslDiff,
    rgbDiff,
    hslNegDiff,
    rgbNegDiff,
    brightnessCondition,
    useOnlyFirstColor,
  ]);

  const [suggestedHeaderFont, setSuggestedHeaderFont] = React.useState(
    availableFonts.includes(detectedFonts[0]?.key)
      ? detectedFonts[0]?.key
      : (detectedFonts.find((f) => availableFonts.includes(f?.key))?.key ?? "Arial")
  );
  const [suggestedBodyFont, setSuggestedBodyFont] = React.useState(
    availableFonts.includes(detectedFonts[1]?.key)
      ? detectedFonts[1]?.key
      : (detectedFonts.find((f) => availableFonts.includes(f?.key))?.key ?? "Arial")
  );
  const selectableFonts = [...detectedFonts.slice(2).filter((f) => availableFonts.includes(f?.key))];
  if (selectableFonts.length === 0) {
    selectableFonts.push("Arial");
  }

  function applySuggestion(sheetKey, colors, headerFont, bodyFont) {
    selectedSheet?.presets.forEach((preset) => {
      if (preset.name.indexOf("Header") >= 0) {
        dispatch(updateSheet({ sheetKey, preset: { name: preset.name, fontFamily: headerFont } }));
      } else dispatch(updateSheet({ sheetKey, preset: { name: preset.name, fontFamily: bodyFont } }));
    });

    colors
      .filter((color) => color.newValue)
      .forEach((color) =>
        dispatch(
          updateSheet({
            sheetKey,
            color: { name: color.name, value: color.newValue },
          })
        )
      );
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateRows: "auto 1fr auto",
        gap: 16,
        containerType: "inline-size",
      }}
    >
      <style>{`
        @container (max-width: 619px) {
          .wizard-columns { grid-template-columns: 1fr minmax(240px, 1fr) !important; }
          .wizard-preview { display: none !important; }
        }
      `}</style>
      {/* Row 1: Title, description, style sheet name */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto" }}>
          <h3 style={{ margin: 0, fontFamily: "Arial", fontWeight: 400, fontSize: 14, color: "black" }}>
            {t("wizard.title")}
          </h3>
          <Menu>
            <MenuTrigger>
              <Tooltip content={t("wizard.menu.algorithm-settings")}>
                <Button
                  size="small"
                  appearance="subtle"
                  icon={<GearIcon width={16} height={16} />}
                  style={{ float: "right" }}
                />
              </Tooltip>
            </MenuTrigger>

            <MenuPopover>
              <CommonTooltip content={t("wizard.menu.reset.tooltip")}>
                <Button
                  size="small"
                  appearance="subtle"
                  style={{ width: "100%", minWidth: 0 }}
                  icon={<ArrowsClockwiseIcon />}
                  onClick={() => {
                    setSuggestedSheetKey(
                      isKeyEditable ? `Custom ${getDominantColor(hexToRgb(detectedColors[2]?.key))}` : selectedSheetKey
                    );
                    setUseHSLSpace(true);
                    setHSLDiff(20);
                    setHSLNegDiff(20);
                    setRGBDiff(0.2);
                    setRGBNegDiff(0.3);
                    setBrightnessCondition(186);
                    setUseOnlyFirstColor(false);
                    setSuggestedText1(0);
                    setSuggestedText2(1);
                    setSuggestedHighlight(2);
                    setSuggestedSecondary(3);
                    setShadingColor("#C0C0C0");
                    setSuggestedHeaderFont(
                      availableFonts.includes(detectedFonts[0]?.key)
                        ? detectedFonts[0]?.key
                        : (detectedFonts.find((f) => availableFonts.includes(f?.key))?.key ?? "Arial")
                    );
                    setSuggestedBodyFont(
                      availableFonts.includes(detectedFonts[1]?.key)
                        ? detectedFonts[1]?.key
                        : (detectedFonts.find((f) => availableFonts.includes(f?.key))?.key ?? "Arial")
                    );
                  }}
                >
                  {t("wizard.menu.reset")}
                </Button>
              </CommonTooltip>
              <CommonTooltip content={t("wizard.menu.first-color-only.tooltip")}>
                <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 2 }}>
                  <div style={{ fontFamily: "Arial", fontWeight: 400, fontSize: 10, color: "black" }}>
                    {t("wizard.menu.first-color-only")}
                  </div>
                  <Switch checked={useOnlyFirstColor} onClick={() => setUseOnlyFirstColor(!useOnlyFirstColor)} />
                </div>
              </CommonTooltip>
              <CommonTooltip content={t("wizard.menu.hsl-space.tooltip")}>
                <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 2 }}>
                  <div style={{ fontFamily: "Arial", fontWeight: 400, fontSize: 10, color: "black" }}>
                    {t("wizard.menu.hsl-space")}
                  </div>
                  <Switch checked={useHSLSpace} onClick={() => setUseHSLSpace(!useHSLSpace)} />
                </div>
              </CommonTooltip>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  gridTemplateRows: "auto auto auto auto auto",
                  gap: 4,
                }}
              >
                {useHSLSpace ? (
                  <>
                    <div style={{ fontFamily: "Arial", fontWeight: 400, fontSize: 10, color: "black" }}>
                      {t("wizard.menu.hsl-diff")}
                    </div>
                    <SpinButton
                      min={0}
                      max={100}
                      value={hslDiff}
                      onChange={(_, d) => setHSLDiff(d.value)}
                      readOnly
                      size="small"
                      style={{ minWidth: 50, width: 70 }}
                    />
                    <div style={{ fontFamily: "Arial", fontWeight: 400, fontSize: 10, color: "black" }}>
                      {t("wizard.menu.hsl-neg-diff")}
                    </div>
                    <SpinButton
                      min={0}
                      max={100}
                      value={hslNegDiff}
                      onChange={(_, d) => setHSLNegDiff(d.value)}
                      readOnly
                      size="small"
                      style={{ minWidth: 50, width: 70 }}
                    />
                  </>
                ) : (
                  <>
                    <div style={{ fontFamily: "Arial", fontWeight: 400, fontSize: 10, color: "black" }}>
                      {t("wizard.menu.rgb-diff")}
                    </div>
                    <SpinButton
                      min={0}
                      max={100}
                      value={rgbDiff * 100}
                      onChange={(_, d) => setRGBDiff(d.value / 100)}
                      readOnly
                      size="small"
                      style={{ minWidth: 50, width: 70 }}
                    />
                    <div style={{ fontFamily: "Arial", fontWeight: 400, fontSize: 10, color: "black" }}>
                      {t("wizard.menu.rgb-neg-diff")}
                    </div>
                    <SpinButton
                      min={0}
                      max={100}
                      value={rgbNegDiff * 100}
                      onChange={(_, d) => setRGBNegDiff(d.value / 100)}
                      readOnly
                      size="small"
                      style={{ minWidth: 50, width: 70 }}
                    />
                  </>
                )}
                <div style={{ fontFamily: "Arial", fontWeight: 400, fontSize: 10, color: "black" }}>
                  {t("wizard.menu.brightness-gate")}
                </div>
                <SpinButton
                  min={0}
                  max={255}
                  value={brightnessCondition}
                  onChange={(_, d) => setBrightnessCondition(d.value)}
                  readOnly
                  size="small"
                  style={{ minWidth: 50, width: 70 }}
                />
              </div>
            </MenuPopover>
          </Menu>
          <Button
            appearance="subtle"
            size="small"
            icon={<XIcon width={16} height={16} />}
            onClick={() => onResult({ choice: "cancel" })}
          />
        </div>

        <div style={{ margin: 0, fontFamily: "Arial", fontWeight: 400, fontSize: 12, color: "black" }}>
          {t("wizard.tooltip.trigger")}{" "}
          <CommonTooltip
            content={
              <div>
                <h4 style={{ fontFamily: "Arial", fontSize: 10, fontWeight: "bold", margin: 0 }}>
                  {t("wizard.tooltip.header")}
                </h4>
                <div style={{ fontFamily: "Arial", fontSize: 10, margin: "4px 0px" }}>{t("wizard.tooltip.p1")}</div>
                <div style={{ fontFamily: "Arial", fontSize: 10, margin: "4px 0px" }}>{t("wizard.tooltip.p2")}</div>
                <div style={{ fontFamily: "Arial", fontSize: 10, margin: "4px 0px" }}>{t("wizard.tooltip.p3")}</div>
              </div>
            }
          >
            <QuestionIcon />
          </CommonTooltip>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 8, alignItems: "center" }}>
          <div style={{ fontFamily: "Arial", fontWeight: 400, fontSize: 10, color: "#212121" }}>
            {t("wizard.sheet-name")}
          </div>
          <input
            style={{ maxWidth: 200 }}
            value={suggestedSheetKey}
            readOnly={!isKeyEditable}
            onChange={(e) => setSuggestedSheetKey(e.target.value)}
          />
          <Button
            appearance="subtle"
            size="small"
            style={{ color: "#0065FD" }}
            onClick={() => {
              // TODO: close this wizard and go to stylesheet editor with hamburger menu open
              onResult({ choice: "choose" });
            }}
          >
            {t("wizard.choose-existing")}
          </Button>
        </div>
      </div>

      {/* Row 2: Fonts | Colors | Preview */}
      <div
        className="wizard-columns"
        style={{ display: "grid", gridTemplateColumns: "1fr minmax(240px, 1fr) 1fr", gap: 8, columnGap: 20 }}
      >
        {/* Column a: Fonts */}
        <div>
          <h5
            style={{ margin: 0, marginBottom: 4, fontFamily: "Arial", fontWeight: 700, fontSize: 12, color: "black" }}
          >
            {t("wizard.fonts")}
          </h5>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: "4px 8px",
              alignItems: "center",
            }}
          >
            {/* Row: Header font */}
            <div style={{ fontFamily: "Arial", fontWeight: 400, fontSize: 11, color: "black" }}>
              {t("wizard.fonts.header")}
            </div>
            <Menu>
              <MenuTrigger>
                <Button
                  size="small"
                  icon={<CaretDownIcon />}
                  iconPosition="after"
                  style={{ minWidth: 0, width: "100%", justifyContent: "space-between" }}
                >
                  <div style={{ fontFamily: suggestedHeaderFont }}>{suggestedHeaderFont}</div>
                </Button>
              </MenuTrigger>
              <MenuPopover style={{ overflowY: "scroll", maxHeight: 250 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {selectableFonts.map((f) => (
                    <Button
                      key={f?.key}
                      appearance="subtle"
                      size="small"
                      style={{ fontFamily: f?.key, justifyContent: "left", color: "black" }}
                      onClick={() => setSuggestedHeaderFont(f?.key)}
                    >
                      {f?.key}
                    </Button>
                  ))}
                  <Divider />
                  {availableFonts
                    .filter((af) => !selectableFonts.find((sf) => sf?.key === af))
                    .sort()
                    .map((f) => (
                      <Button
                        key={f}
                        appearance="subtle"
                        size="small"
                        style={{ fontFamily: f, justifyContent: "left", color: "black" }}
                        onClick={() => setSuggestedHeaderFont(f)}
                      >
                        {f}
                      </Button>
                    ))}
                </div>
              </MenuPopover>
            </Menu>

            {/* Row: Body font */}
            <div style={{ fontFamily: "Arial", fontWeight: 400, fontSize: 11, color: "black" }}>
              {t("wizard.fonts.body")}
            </div>
            <Menu>
              <MenuTrigger>
                <Button
                  size="small"
                  icon={<CaretDownIcon />}
                  iconPosition="after"
                  style={{ minWidth: 0, width: "100%", justifyContent: "space-between" }}
                >
                  <div style={{ fontFamily: suggestedBodyFont }}>{suggestedBodyFont}</div>
                </Button>
              </MenuTrigger>
              <MenuPopover style={{ overflowY: "scroll", maxHeight: 250 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {selectableFonts.map((f) => (
                    <Button
                      key={f?.key}
                      appearance="subtle"
                      size="small"
                      style={{ fontFamily: f?.key, justifyContent: "left", color: "black" }}
                      onClick={() => setSuggestedBodyFont(f?.key)}
                    >
                      {f?.key}
                    </Button>
                  ))}
                  <Divider />
                  {availableFonts
                    .filter((af) => !selectableFonts.find((sf) => sf?.key === af))
                    .sort()
                    .map((f) => (
                      <Button
                        key={f}
                        appearance="subtle"
                        size="small"
                        style={{ fontFamily: f, justifyContent: "left", color: "black" }}
                        onClick={() => setSuggestedBodyFont(f)}
                      >
                        {f}
                      </Button>
                    ))}
                </div>
              </MenuPopover>
            </Menu>
          </div>
          {selectableFonts.length < detectedFonts.length - 2 && (
            <div
              style={{
                fontFamily: "Arial",
                fontWeight: 400,
                fontSize: 10,
                color: "#D40000",
                background: "#F8F8F8",
                marginTop: 4,
                lineHeight: 1.2,
              }}
            >
              <WarningIcon /> {t("wizard.fonts.unavailable")}{" "}
              <CommonTooltip
                content={
                  <div>
                    <div
                      style={{
                        marginBottom: 12,
                        fontFamily: "Arial",
                        fontWeight: "bold",
                        fontSize: 10,
                        color: "black",
                      }}
                    >
                      {t("wizard.fonts.unavailable.tooltip.header")}
                    </div>
                    {detectedFonts
                      .slice(2)
                      .filter((f) => !availableFonts.includes(f?.key))
                      .map((f) => (
                        <div
                          key={f?.key}
                          style={{ fontFamily: "Arial", fontWeight: 400, fontSize: 12, color: "black" }}
                        >
                          {f?.key}
                        </div>
                      ))}
                    <div style={{ marginTop: 12, fontFamily: "Arial", fontWeight: 400, fontSize: 10, color: "black" }}>
                      {t("wizard.fonts.unavailable.tooltip.footer")}
                    </div>
                  </div>
                }
                showDelay={500}
              >
                <InfoIcon />
              </CommonTooltip>
            </div>
          )}
        </div>

        {/* Column b: Colors */}
        <div>
          <h5
            style={{ margin: 0, marginBottom: 8, fontFamily: "Arial", fontWeight: 700, fontSize: 12, color: "black" }}
          >
            {t("wizard.colors")}
          </h5>

          {/* Borderless table: label | Highlight | Secondary | Shading */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr 1fr 1fr",
              gap: "8px 8px",
              alignItems: "center",
            }}
          >
            {/* Row 1: Accents – dropdowns */}
            <div style={{ fontFamily: "Arial", fontWeight: 400, fontSize: 11, color: "black" }}>
              {t("wizard.colors.accents")}
            </div>

            {/* Highlight 1 dropdown */}
            <Menu positioning="above">
              <MenuTrigger>
                <Button
                  size="small"
                  icon={<CaretDownIcon />}
                  iconPosition="after"
                  style={{ minWidth: 0, padding: "2px 4px" }}
                >
                  <div
                    style={{
                      background: availableColors[suggestedHighlight]?.key || "white",
                      border: "1px solid #B8B8B8",
                      borderRadius: 2,
                      width: 16,
                      height: 16,
                    }}
                  />
                </Button>
              </MenuTrigger>
              <MenuPopover>
                <div style={{ display: "flex", flexDirection: "row", gap: 4, flexWrap: "wrap", maxWidth: 200 }}>
                  {availableColors.slice(2).map((dc, i) => (
                    <Button
                      key={dc?.key}
                      onClick={() => setSuggestedHighlight(i + 2)}
                      style={{ minWidth: 0, padding: 0 }}
                      appearance="subtle"
                    >
                      <div
                        style={{
                          background: dc?.key || "white",
                          border: i === suggestedHighlight - 2 ? "1px dashed #0065FD" : "1px solid #B8B8B8",
                          borderRadius: 2,
                          width: 16,
                          height: 16,
                        }}
                      />
                    </Button>
                  ))}
                </div>
              </MenuPopover>
            </Menu>

            {/* Secondary 1 dropdown */}
            <Menu positioning="above">
              <MenuTrigger>
                <Button
                  size="small"
                  icon={<CaretDownIcon />}
                  iconPosition="after"
                  style={{ minWidth: 0, padding: "2px 4px" }}
                >
                  <div
                    style={{
                      background: availableColors[suggestedSecondary]?.key || "white",
                      border: "1px solid #B8B8B8",
                      borderRadius: 2,
                      width: 16,
                      height: 16,
                    }}
                  />
                </Button>
              </MenuTrigger>
              <MenuPopover>
                <div style={{ display: "flex", flexDirection: "row", gap: 4, flexWrap: "wrap", maxWidth: 200 }}>
                  {availableColors.slice(2).map((dc, i) => (
                    <Button
                      key={dc?.key}
                      onClick={() => setSuggestedSecondary(i + 2)}
                      style={{ minWidth: 0, padding: 0 }}
                      appearance="subtle"
                    >
                      <div
                        style={{
                          background: dc?.key || "white",
                          border: i === suggestedSecondary - 2 ? "1px dashed #0065FD" : "1px solid #B8B8B8",
                          borderRadius: 2,
                          width: 16,
                          height: 16,
                        }}
                      />
                    </Button>
                  ))}
                </div>
              </MenuPopover>
            </Menu>

            {/* Shading 2 dropdown */}
            <Menu positioning="above">
              <MenuTrigger>
                <Button
                  size="small"
                  icon={<CaretDownIcon />}
                  iconPosition="after"
                  style={{ minWidth: 0, padding: "2px 4px" }}
                >
                  <div
                    style={{
                      background: shadingColor || "white",
                      border: "1px solid #B8B8B8",
                      borderRadius: 2,
                      width: 16,
                      height: 16,
                    }}
                  />
                </Button>
              </MenuTrigger>
              <MenuPopover>
                <div style={{ display: "flex", flexDirection: "row", gap: 4, flexWrap: "wrap", maxWidth: 200 }}>
                  {[{ key: "#C0C0C0" }, ...availableColors.slice(2)].map((dc) => (
                    <Button
                      key={dc?.key}
                      onClick={() => setShadingColor(dc?.key)}
                      style={{ minWidth: 0, padding: 0 }}
                      appearance="subtle"
                    >
                      <div
                        style={{
                          background: dc?.key || "white",
                          border: dc?.key === shadingColor ? "1px dashed #0065FD" : "1px solid #B8B8B8",
                          borderRadius: 2,
                          width: 16,
                          height: 16,
                        }}
                      />
                    </Button>
                  ))}
                </div>
              </MenuPopover>
            </Menu>

            {/* Row 2: Generated accents – previews */}
            <div
              style={{
                fontFamily: "Arial",
                fontWeight: 400,
                fontSize: 11,
                color: "black",
                maxWidth: 48,
                lineHeight: 1.2,
              }}
            >
              {t("wizard.colors.generated-accents")}
            </div>
            {["accent2", "secondary2", "shading1"].map((name) => {
              const color = suggestedColors.find((c) => c.name === name);
              if (!color) return <div key={name} />;
              const c = color.newValue ?? color.value;
              const { r, g, b } = hexToRgb(c);
              const br = brightness(r, g, b);
              return (
                <CommonTooltip
                  key={name}
                  content={
                    <div>
                      <div>{t("menu.style.colors." + name)}</div>
                      {color.newValue ? (
                        <div>
                          {color.value} {"=>"} {color.newValue}
                        </div>
                      ) : (
                        <div>{color.value}</div>
                      )}
                    </div>
                  }
                >
                  <div
                    style={{
                      background: c,
                      border: br > 187.0 ? "1px solid #B8B8B8" : "1px solid " + c,
                      width: 16,
                      height: 16,
                      borderRadius: 2,
                      marginLeft: 6,
                    }}
                  />
                </CommonTooltip>
              );
            })}

            {/* Row 3: Other colors */}
            <div
              style={{
                fontFamily: "Arial",
                fontWeight: 400,
                fontSize: 11,
                color: "black",
                alignSelf: "start",
                paddingTop: 2,
              }}
            >
              {t("wizard.colors.other")}
            </div>
            <div
              style={{
                gridColumn: "2 / span 3",
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 4,
                paddingLeft: 6,
              }}
            >
              {suggestedColors
                .filter(
                  (c) => !["accent1", "accent2", "secondary1", "secondary2", "shading1", "shading2"].includes(c.name)
                )
                .map((color) => {
                  const c = color.newValue ?? color.value;
                  const { r, g, b } = hexToRgb(c);
                  const br = brightness(r, g, b);
                  return (
                    <CommonTooltip
                      key={color.name}
                      content={
                        <div>
                          <div>{t("menu.style.colors." + color.name)}</div>
                          <div>{color.value}</div>
                        </div>
                      }
                    >
                      <div
                        style={{
                          background: c,
                          border: br > 187.0 ? "1px solid #B8B8B8" : "1px solid " + c,
                          width: 16,
                          height: 16,
                          borderRadius: 2,
                        }}
                      />
                    </CommonTooltip>
                  );
                })}
            </div>

            {/* Row 4: Edit note */}
            <div />
            <div
              style={{
                gridColumn: "2 / span 3",
              }}
            >
              <div
                style={{
                  fontFamily: "Arial",
                  fontWeight: 400,
                  fontSize: 10,
                  color: "#555555",
                  paddingLeft: 6,
                  lineHeight: 1.2,
                }}
              >
                {t("wizard.customize-note")}
              </div>
              {detectedColors.length < 5 && (
                <div
                  style={{
                    fontFamily: "Arial",
                    fontWeight: 400,
                    fontSize: 10,
                    color: "#D40000",
                    background: "#F8F8F8",
                    marginTop: 8,
                    lineHeight: 1.2,
                  }}
                >
                  <WarningIcon /> {t("wizard.colors.too-few-found")}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Column c: Preview */}
        <div className="wizard-preview" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ fontFamily: "Arial", fontWeight: 700, fontSize: 12, color: "black" }}>
            {t("wizard.preview")}
          </div>
          <PreviewTable
            suggestedColors={suggestedColors}
            suggestedHeaderFont={suggestedHeaderFont}
            suggestedBodyFont={suggestedBodyFont}
          />
        </div>
      </div>

      {/* Row 3: Bottom text + buttons */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "right",
            gap: 4,
            fontFamily: "Arial",
            fontSize: "10px",
            color: "black",
          }}
        >
          {t("wizard.bottom-line")} <NewStyleSheet style={{ width: 16, height: 16 }} />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 6,
            justifyContent: "right",
          }}
        >
          <Button
            appearance="outline"
            size="small"
            style={{ border: "1px solid #0065FD", color: "#0065FD" }}
            onClick={() => {
              applySuggestion(suggestedSheetKey, suggestedColors, suggestedHeaderFont, suggestedBodyFont);
              onResult({ choice: "customize", suggestedSheetKey });
            }}
          >
            {t("wizard.customize")}
          </Button>
          <Button
            appearance="primary"
            size="small"
            style={{ background: "#0065FD", border: "1px solid #0065FD" }}
            onClick={() => {
              applySuggestion(suggestedSheetKey, suggestedColors, suggestedHeaderFont, suggestedBodyFont);
              onResult({ choice: "set", suggestedSheetKey });
            }}
          >
            {t("wizard.set")}
          </Button>
        </div>
      </div>
    </div>
  );
};

StyleSheetWizard.propTypes = {
  detectedFonts: PropTypes.array.isRequired,
  availableFonts: PropTypes.array.isRequired,
  detectedColors: PropTypes.array.isRequired,
  existingColors: PropTypes.array.isRequired,
  selectedSheetKey: PropTypes.string,
  onResult: PropTypes.func.isRequired,
};

export default StyleSheetWizard;
